<script setup lang="ts">
import OrganizationAutoComplate from '@/components/AutoCompletes/OrganizationAutoComplate.vue'
import DepartmentSelectBox from '@/components/UserRoles/DepartmentSelectBox.vue'
import {
  userRoleSearchFormType,
  officeType,
  departmentType
} from '@/types/requests/userRoleSearchForm'
import { Ref, ref, computed, onMounted, watch } from 'vue'
import SelectRoleChecker from '@/router/selectRoleChecker'
import {
  MANAGEMENT_OFFICE,
  SUB_MANAGEMENT_OFFICE,
  INSTALLATION_OFFICE,
  OPERATION_OFFICE,
  DEPARTMENT
} from "@/services/consts/user_role";
import i18n from '@/lang/i18n';
import { 
  saveDataToSessionStorage, 
  loadDataFromSessionStorage, 
  clearSessionStorage 
} from "@/services/functions/sessionStorage";

const showableOfficeableTypes: Ref<officeType[]> = ref([])
const selectedOfficeableType: Ref<officeType> = ref({id:null, name:"", type:"", departments: []})
const isDepartment = ref(false)
const organization_id = ref()
const organization_type = ref("" as string)

const emits = defineEmits([
  'update:modelValue'
])
const props = defineProps<{
  modelValue: userRoleSearchFormType
  errors: {
    organization: string[],
    department: string[],
    display_name: string[],
  }
}>()

defineExpose({
  resetOfficeableType,
  saveFormValuesToSessionStorage,
  clearSessionStorage
})

const modelValue = computed({
  get() {
    return props.modelValue;
  },
  set(value) {
    emits('update:modelValue', value);
  },
})

onMounted(() => {
  const current_role = SelectRoleChecker.getCurrentRole()
  if (current_role?.organization_id) {
    organization_id.value = current_role.organization_id
  }
  if (current_role?.organization_id) {
    organization_type.value = current_role.organization_type
  }
  if (organization_type.value == DEPARTMENT.type) {
    isDepartment.value = true
  }
  setShowableOfficeType()
  const savedFormValues = loadDataFromSessionStorage('userRolesSearchFormContent');
    if (savedFormValues) {
      selectedOfficeableType.value = savedFormValues.officeable_type;
      modelValue.value.organization = savedFormValues.organization;
      modelValue.value.department = savedFormValues.department;
      modelValue.value.display_name = savedFormValues.display_name;
    }
  watch(() => modelValue.value.organization, (newValue) => {
    const isIncluded = newValue?.departments.some(department => department.id === modelValue.value?.department?.id)
    if (!isIncluded) {
      modelValue.value.department = []
    }
  })
})

function setShowableOfficeType(){
  const org_type = organization_type.value.split('\\')[2];
  const managementOffice = convertOfficeableType(MANAGEMENT_OFFICE)
  const subManagementOffice = convertOfficeableType(SUB_MANAGEMENT_OFFICE)
  const installationOffice = convertOfficeableType(INSTALLATION_OFFICE)
  const operationOffice = convertOfficeableType(OPERATION_OFFICE)
  const department = convertOfficeableType(DEPARTMENT)

  switch (org_type) {
    case managementOffice.type:
      showableOfficeableTypes.value = [
        managementOffice,
        subManagementOffice,
        installationOffice,
        operationOffice
      ]
      break;
    case subManagementOffice.type:
      showableOfficeableTypes.value = [
        subManagementOffice,
        installationOffice,
        operationOffice
      ]
      break;
    case installationOffice.type:
      showableOfficeableTypes.value = [
        installationOffice,
        operationOffice
      ]
      break;
    case operationOffice.type:
      showableOfficeableTypes.value = [
        operationOffice
      ]
      break;
    case department.type:
      showableOfficeableTypes.value = [
        operationOffice
      ]
      break;
    default:
      showableOfficeableTypes.value = [];
  }
}

function convertOfficeableType(office: {id: number, name: string, type: string}){
  let formated = JSON.parse(JSON.stringify(office))
  formated.type = office.type.split('\\')[2];
  return formated
}

function resetOfficeableType(){
  selectedOfficeableType.value = { id: null, name: "", type: "" }
}

function saveFormValuesToSessionStorage() {
  saveDataToSessionStorage('userRolesSearchFormContent', {
    officeable_type: selectedOfficeableType.value,
    organization: modelValue.value.organization,
    department: modelValue.value.department,
    display_name: modelValue.value.display_name,
  });
}

</script>
<template>
  <v-select
    class="mb-2"
    v-model="selectedOfficeableType"
    :items="showableOfficeableTypes"
    item-title="name"
    item-value="id"
    v-bind="{'return-object': true}"
    :label="i18n.global.t('pages.userRoles.search.officeType')"
    :clearable="false"
  />
  <OrganizationAutoComplate
    class="mb-2"
    v-model="modelValue.organization"
    :selectedOfficeableType="selectedOfficeableType"
    :errorMessages="errors?.organization"
  />
  <DepartmentSelectBox
    v-model="modelValue.department"
    :organization="modelValue.organization"
    :error-messages="errors?.department"
    :isEdit="false"
    :isDepartment="isDepartment"
    :clearable="true"
  />
  <v-text-field
    :label="i18n.global.t('pages.userRoles.search.role')"
    v-model="modelValue.display_name"
    :error-messages="errors?.display_name"
  ></v-text-field>
</template>
